/**
 * Push variant shape and bounds, kept OUT of the .tsx so the editor module
 * exports only its component — `react-refresh/only-export-components` is a
 * lint error under the portal's `--max-warnings 0` gate.
 */

/**
 * Hard caps, mirroring the strata service (a longer value is a 400). They are
 * SANITY bounds, not OneSignal's rule: OneSignal publishes no character limit
 * on headings/contents, so the cap exists to stop an essay being pasted into a
 * 2000-alias request body.
 */
export const PUSH_TITLE_MAX = 250;
export const PUSH_BODY_MAX = 2000;

/**
 * Where the phone actually truncates. Roughly what Android and iOS show in a
 * collapsed notification before an ellipsis; past this the copy still SENDS,
 * it just stops being read. Surfaced as a soft warning rather than a block —
 * an expanded notification does show more, so this is guidance, not a rule.
 */
export const PUSH_TITLE_VISIBLE = 65;
export const PUSH_BODY_VISIBLE = 240;

export interface PushVariant {
  title: string;
  body: string;
}

export const EMPTY_PUSH_VARIANT: PushVariant = { title: '', body: '' };

/**
 * Push content is BATCH-level: one OneSignal publish carries one title and one
 * body for up to 2000 recipients, so per-recipient substitution is impossible
 * on this channel and the service rejects it with a 400. Caught here too, so
 * the operator finds out while typing instead of on submit.
 */
export function pushVariantError(variant: PushVariant): string | null {
  if (!variant.title.trim() || !variant.body.trim()) {
    return 'Please enter a push title and body';
  }
  if (variant.title.length > PUSH_TITLE_MAX) {
    return `Push title exceeds ${PUSH_TITLE_MAX} characters`;
  }
  if (variant.body.length > PUSH_BODY_MAX) {
    return `Push body exceeds ${PUSH_BODY_MAX} characters`;
  }
  if (
    variant.title.includes('{{first_name}}') ||
    variant.body.includes('{{first_name}}') ||
    variant.title.includes('{{surname}}') ||
    variant.body.includes('{{surname}}')
  ) {
    return 'Push content cannot use {{first_name}} or {{surname}} — one push is sent to a whole batch of recipients, so it carries the same title and body for all of them. {{brand}} is supported.';
  }
  return null;
}

export function isPushVariantComplete(variant: PushVariant): boolean {
  return pushVariantError(variant) === null;
}
